/**
 * useUserCRUD Hook
 *
 * 사용자(멤버) CRUD 작업을 위한 React Hook
 *
 * 책임:
 * - 사용자 데이터 로드
 * - 사용자 생성(초대)/수정/삭제
 * - 로딩/에러 상태 관리
 * - 역할 관리 (Owner 최소 1명 유지 등)
 */

import { useState } from 'react'
import type { User } from '../types/User'
import { userRepository } from '../repositories'
import {
  canChangeUserRole,
  canRemoveUser,
  checkMinimumOwners,
} from '../utils/userPermissions'

/**
 * useUserCRUD Return Type
 */
export interface UseUserCRUD {
  /** 사용자 목록 */
  data: User[]
  /** 로딩 상태 */
  loading: boolean
  /** 에러 상태 */
  error: Error | null
  /** 전체 사용자 로드 */
  loadAll: () => Promise<void>
  /** 활성 사용자만 로드 */
  loadActive: () => Promise<void>
  /** 새 사용자 생성 (초대) */
  create: (input: Omit<User, 'id'>) => Promise<User>
  /** 사용자 정보 수정 (역할 변경 등) - BUG-009: currentUser 파라미터 추가 */
  update: (id: string, input: Partial<User>, currentUser: User) => Promise<User>
  /** 사용자 삭제 (멤버 제거) - BUG-009: currentUser 파라미터 추가 */
  remove: (id: string, currentUser: User) => Promise<void>
}

/**
 * 사용자 CRUD Hook
 *
 * @returns CRUD 작업 메서드 및 상태
 *
 * @example
 * ```tsx
 * function MembersPage() {
 *   const { data, loading, error, loadAll, create, update, remove } = useUserCRUD()
 *
 *   useEffect(() => {
 *     loadAll()
 *   }, [])
 *
 *   const handleInvite = async () => {
 *     await create({
 *       email: 'newuser@example.com',
 *       name: '새 멤버',
 *       role: 'member',
 *       isActive: true,
 *       joinedAt: new Date().toISOString()
 *     })
 *     await loadAll()
 *   }
 *
 *   const handleChangeRole = async (userId: string) => {
 *     // Owner 최소 1명 체크
 *     const owners = data.filter(u => u.role === 'owner')
 *     if (owners.length === 1 && owners[0].id === userId) {
 *       alert('최소 1명의 Owner가 필요합니다')
 *       return
 *     }
 *     await update(userId, { role: 'owner' })
 *     await loadAll()
 *   }
 * }
 * ```
 */
export function useUserCRUD(): UseUserCRUD {
  const [data, setData] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  /**
   * 전체 사용자 로드
   */
  const loadAll = async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await userRepository.findAll()
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('알 수 없는 오류'))
      throw err
    } finally {
      setLoading(false)
    }
  }

  /**
   * 활성 사용자만 로드
   */
  const loadActive = async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await userRepository.findActive()
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('알 수 없는 오류'))
      throw err
    } finally {
      setLoading(false)
    }
  }

  /**
   * 새 사용자 생성 (초대)
   */
  const create = async (input: Omit<User, 'id'>): Promise<User> => {
    try {
      setLoading(true)
      setError(null)
      const created = await userRepository.create(input)
      return created
    } catch (err) {
      setError(err instanceof Error ? err : new Error('알 수 없는 오류'))
      throw err
    } finally {
      setLoading(false)
    }
  }

  /**
   * 사용자 정보 수정 (권한 검증 포함)
   *
   * @fix BUG-009: 권한 검증 로직 추가
   * @why Owner만 다른 유저의 역할을 변경할 수 있어야 하며,
   *      Owner는 자신의 권한을 스스로 낮출 수 없습니다.
   */
  const update = async (
    id: string,
    input: Partial<User>,
    currentUser: User
  ): Promise<User> => {
    try {
      setLoading(true)
      setError(null)

      // 1. 대상 유저 조회
      const target = await userRepository.findById(id)
      if (!target) {
        throw new Error('대상 사용자를 찾을 수 없습니다')
      }

      // 2. 역할 변경 시 권한 검증
      if (input.role && input.role !== target.role) {
        // 권한 검증
        if (!canChangeUserRole(currentUser, target, input.role)) {
          throw new Error(
            '권한이 없습니다. Owner만 역할을 변경할 수 있습니다.'
          )
        }

        // 3. 최소 Owner 수 검증 (Owner → Member 강등 시)
        if (target.role === 'owner' && input.role === 'member') {
          if (!checkMinimumOwners(data, target, 'demote')) {
            throw new Error('최소 1명의 Owner가 필요합니다')
          }
        }
      }

      // 4. 실제 업데이트
      const updated = await userRepository.update(id, input)
      return updated
    } catch (err) {
      setError(err instanceof Error ? err : new Error('알 수 없는 오류'))
      throw err
    } finally {
      setLoading(false)
    }
  }

  /**
   * 사용자 삭제 (멤버 제거) - 권한 검증 포함
   *
   * @fix BUG-009: 권한 검증 로직 추가
   * @why Owner만 member를 제거할 수 있으며, Owner는 제거할 수 없습니다.
   */
  const remove = async (id: string, currentUser: User): Promise<void> => {
    try {
      setLoading(true)
      setError(null)

      // 1. 대상 유저 조회
      const target = await userRepository.findById(id)
      if (!target) {
        throw new Error('대상 사용자를 찾을 수 없습니다')
      }

      // 2. 권한 검증
      if (!canRemoveUser(currentUser, target)) {
        if (currentUser.role !== 'owner') {
          throw new Error(
            '권한이 없습니다. Owner만 멤버를 제거할 수 있습니다.'
          )
        } else {
          throw new Error('Owner는 제거할 수 없습니다')
        }
      }

      // 3. 실제 삭제
      await userRepository.delete(id)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('알 수 없는 오류'))
      throw err
    } finally {
      setLoading(false)
    }
  }

  return {
    data,
    loading,
    error,
    loadAll,
    loadActive,
    create,
    update,
    remove,
  }
}
